// Shared carry-forward seeding for the daily-report forms (Cost Log, Personnel
// Log, …). A per-row `carryForward` flag pins a row to seed the NEXT day for the
// same job — a sticky chain (PO 2026-07-11): the seeded copy keeps the flag, so
// a steady cost/crew rolls forward each new day until the user unchecks it or
// deletes the row.
//
// ONE generic so the two forms cannot drift on the (subtle) preceding-day rule —
// each form supplies only its `hasContent` predicate and row-copier.

/** Minimal shape of a prior day-record this seeder reads. `data` is left loose
 *  (the forms store `Record<string, unknown>`); the reader narrows date/entries. */
export interface CarryForwardReport<T> {
  id: string;
  type: string;
  data?:
    | { date?: string | undefined; entries?: T[] | undefined }
    | Record<string, unknown>
    | undefined;
}

interface CarryForwardOptions<T extends { carryForward?: boolean }> {
  /** The report `type` these days share (e.g. "cost-log"). */
  reportType: string;
  /** True when an entry holds real user content (not a blank scaffold row). */
  hasContent: (entry: T) => boolean;
  /** Clone a flagged row into a fresh row for the new day (keeps the flag). */
  copyRow: (prev: T) => T;
}

/**
 * Seed a new day from the immediately-preceding WORKED day's flagged rows.
 *
 * "Preceding worked day" = the most-recent record of this type, strictly before
 * the active day, that holds real content — the active day itself excluded.
 * Consulting only that one day preserves the sticky-chain mental model: if the
 * user unchecked the row there (the day still has content), the chain breaks and
 * nothing carries. Content-empty scaffold days are SKIPPED, so a weekend/holiday
 * gap — or an abandoned blank "Add Day" record — does not break the chain
 * (Friday still seeds Monday).
 *
 * Returns null when there is no active date, no preceding worked day, or that
 * day has no flagged CONTENT rows (a blank flagged row seeds nothing).
 */
export function selectCarryForwardRows<T extends { carryForward?: boolean }>(
  existingReports: CarryForwardReport<T>[] | undefined,
  activeDayId: string | null,
  activeDate: string | undefined,
  { reportType, hasContent, copyRow }: CarryForwardOptions<T>,
): T[] | null {
  const activeYmd = (activeDate ?? "").slice(0, 10);
  // No active date → don't guess a source (never carry a future day backward).
  if (!activeYmd || !existingReports || existingReports.length === 0)
    return null;

  const precedingDay = existingReports
    .filter((r) => r.type === reportType && r.id !== activeDayId)
    .map((r) => {
      const data = (r.data ?? {}) as { date?: unknown; entries?: unknown };
      return {
        date: String(data.date ?? "").slice(0, 10),
        entries: (Array.isArray(data.entries) ? data.entries : []) as T[],
      };
    })
    // Strictly before the active day (YYYY-MM-DD lexical compare == chronological)…
    .filter((d) => d.date && d.date < activeYmd)
    // …and actually WORKED — skips blank scaffold days so a gap can't break the chain.
    .filter((d) => d.entries.some(hasContent))
    .sort((a, b) => b.date.localeCompare(a.date))[0];

  if (!precedingDay) return null;
  const flagged = precedingDay.entries.filter(
    (e) => e.carryForward && hasContent(e),
  );
  return flagged.length > 0 ? flagged.map(copyRow) : null;
}
